import { Button, Form, FormProps, Input, Table, TableProps } from "antd";
import { useGetCascadeProcessQuery, useUpdateCascadeProcessMutation } from "api/TenantSlug/CascadeProcess/hooks";
import { CascadeFailed } from "components/UI/Cascade/CascadeFailed.tsx";
import { CascadeLoading } from "components/UI/Cascade/CascadeLoading.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { ValidateErrorEntity } from "rc-field-form/lib/interface";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { CascadeProcessItemAction } from "types/enums.ts";

import GetCascadeProcessItemResponse = App.Http.Responses.Cascade.GetCascadeProcessItemResponse;
import GetCascadeProcessResponse = App.Http.Responses.Cascade.GetCascadeProcessResponse;

interface CascadeRedirectProps {
    cascade: GetCascadeProcessResponse;
    tenantSlug: string;
    processId: string;
}

interface FieldType {
    smsCode: string;
}

export const CascadeRedirect = ({ cascade, tenantSlug, processId }: CascadeRedirectProps) => {
    const { t } = usePaldockTranslation("ui.cascade");
    const [form] = Form.useForm();

    // Check if ciid (cascade item id) is in URL params (opened from new tab)
    const urlParams = new URLSearchParams(window.location.search);
    const cascadeItemIdFromUrl = urlParams.get("ciid");

    const redirectItems = useMemo(
        () =>
            cascade.items?.filter(
                (redirectItem) =>
                    ((redirectItem.action === CascadeProcessItemAction.Redirect ||
                        redirectItem.action === CascadeProcessItemAction.PostVerification) &&
                        redirectItem.id !== null) ||
                    redirectItem.action === CascadeProcessItemAction.Visible
            ),
        [cascade.items]
    );
    const [postVerificationItem, setPostVerificationItem] = useState<GetCascadeProcessItemResponse | null>(null);
    const updateCascadeProcessMutation = useUpdateCascadeProcessMutation();
    const getCascadeProcessQuery = useGetCascadeProcessQuery(processId, tenantSlug, {
        enabled: false,
    });
    const hasAutoSelected = useRef(false);
    const isMounted = useRef(true);

    const isSinglePostVerification =
        redirectItems &&
        redirectItems.length === 1 &&
        redirectItems[0].action === CascadeProcessItemAction.PostVerification;

    useEffect(() => {
        if (isSinglePostVerification && !hasAutoSelected.current && redirectItems?.[0]) {
            setPostVerificationItem(redirectItems[0]);
            hasAutoSelected.current = true;
        } else if (!isSinglePostVerification) {
            setPostVerificationItem(null);
            hasAutoSelected.current = false;
        }
    }, [isSinglePostVerification]);

    // Auto-select item from URL param if present (opened from new tab)
    useEffect(() => {
        if (cascadeItemIdFromUrl && redirectItems && !hasAutoSelected.current) {
            const itemFromUrl = redirectItems.find((item) => item.id === cascadeItemIdFromUrl);
            if (itemFromUrl && itemFromUrl.action === CascadeProcessItemAction.PostVerification) {
                setPostVerificationItem(itemFromUrl);
                hasAutoSelected.current = true;
            } else {
                // ciid not found or wrong action - mark as attempted, show list instead of loading
                hasAutoSelected.current = true;
                if (!itemFromUrl) {
                    toast.error(t("messages.verification_item_not_found"));
                    console.error("verification_item_not_found");
                }
            }
        }
    }, [cascadeItemIdFromUrl, redirectItems, t]);

    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    const onFinish: FormProps<FieldType>["onFinish"] = (values) => {
        if (!postVerificationItem || !postVerificationItem.id) {
            return;
        }

        updateCascadeProcessMutation.mutate(
            {
                cascadeProcessId: processId,
                action: CascadeProcessItemAction.PostVerification,
                cascadeItemId: postVerificationItem.id,
                request: {
                    code: values.smsCode,
                },
            },
            {
                onSuccess: () => {
                    getCascadeProcessQuery
                        .refetch()
                        .then((result) => {
                            if (!isMounted.current) return;

                            const refetchedCascade = result.data?.data as GetCascadeProcessResponse | undefined;
                            const refetchedItem = refetchedCascade?.items?.find(
                                (item) => item.id === postVerificationItem.id
                            );

                            if (!refetchedItem) {
                                // Item missing after refetch - verification error
                                toast.error(t("messages.verification_error_contact_support"));
                                console.error("verification_error_contact_support");
                            } else if (refetchedItem.action === CascadeProcessItemAction.PostVerification) {
                                // Action didn't change - bad verification code
                                toast.error(t("messages.phone_verification_failed"));
                                console.error("phone_verification_failed");
                            } else if (refetchedItem.action === CascadeProcessItemAction.Failed) {
                                // Changed to failed - verification error
                                toast.error(t("messages.verification_error_contact_support"));
                                console.error("verification_error_contact_support");
                            } else {
                                // Action changed to redirect/priority_redirect etc - success
                                toast.success(t("messages.phone_number_verified"));

                                // If opened from new tab (ciid in URL) and action is Redirect, auto-redirect
                                if (
                                    cascadeItemIdFromUrl &&
                                    refetchedItem.action === CascadeProcessItemAction.Redirect &&
                                    refetchedItem.id
                                ) {
                                    window.location.href = `${import.meta.env.VITE_BACKEND_URL ?? "localhost"}/api/${tenantSlug}/processes/${processId}/${CascadeProcessItemAction.Redirect}/${refetchedItem.id}`;
                                } else {
                                    setPostVerificationItem(null);
                                }
                            }
                        })
                        .catch(() => {
                            if (!isMounted.current) return;
                            toast.error(t("messages.verification_error_contact_support"));
                            console.error("verification_error_contact_support");
                        });
                },
                onError: (errorResponse) => {
                    if (!isMounted.current) return;
                    console.error(errorResponse);
                    toast.error(t("messages.verification_error_contact_support"));
                },
                onSettled: () => {
                    if (!isMounted.current) return;
                    form.resetFields();
                },
            }
        );
    };

    const onFinishFailed: FormProps<FieldType>["onFinishFailed"] = (errorInfo: ValidateErrorEntity) => {
        if (errorInfo.errorFields.find((item) => item.name.toString() === "smsCode")) {
            toast.error(t("messages.enter_verification_sms"));
        } else {
            toast.error(t("messages.verification_error_contact_support"));
        }
        console.log("Failed", errorInfo);
    };

    const handleRedirect = async (cascadeItem: GetCascadeProcessItemResponse) => {
        if (!cascadeItem.id) {
            return;
        }

        window.open(
            `${import.meta.env.VITE_BACKEND_URL ?? "localhost"}/api/${tenantSlug}/processes/${processId}/${CascadeProcessItemAction.Redirect}/${cascadeItem.id}`
        );
    };

    const handlePostVerification = async (cascadeItem: GetCascadeProcessItemResponse) => {
        if (!cascadeItem.id) {
            return;
        }

        if (cascadeItem.action === CascadeProcessItemAction.PostVerification) {
            // Open in new tab with ciid (cascade item id) query param (trusted event)
            const currentUrl = new URL(window.location.href);
            currentUrl.searchParams.set("ciid", cascadeItem.id);
            window.open(currentUrl.toString(), "_blank");
        }
    };

    if (!redirectItems) {
        return <CascadeFailed />;
    } else {
        const columns: TableProps<GetCascadeProcessItemResponse>["columns"] = [
            {
                title: t("columns.title"),
                dataIndex: "title",
                render: (title: string) => <div className="flex  justify-center">{title}</div>,
            },
            {
                title: t("columns.actions"),
                dataIndex: "action",
                render: (_, record: GetCascadeProcessItemResponse) => {
                    if (record.action === CascadeProcessItemAction.Redirect) {
                        return (
                            <div className="flex  justify-center">
                                <Button
                                    type="primary"
                                    id={`nextButtonStep${record.id}`}
                                    onClick={() => handleRedirect(record)}
                                >
                                    {t("labels.continue")}
                                </Button>
                            </div>
                        );
                    } else if (record.action === CascadeProcessItemAction.PostVerification) {
                        return (
                            <div className="flex  justify-center">
                                <Button
                                    type="primary"
                                    id={`verificationButtonStep${record.id}`}
                                    onClick={() => handlePostVerification(record)}
                                >
                                    {t("labels.continue")}
                                </Button>
                            </div>
                        );
                    } else if (record.action === CascadeProcessItemAction.Visible) {
                        return (
                            <div className="flex justify-center">
                                <Button type="default" disabled id={`visibleButton${record.id}`}>
                                    {t("labels.done")}
                                </Button>
                            </div>
                        );
                    }
                },
            },
        ];
        return (
            <div className="w-full max-w-ws">
                <div id="step-1" className="step step-1">
                    {postVerificationItem ? (
                        <>
                            <h1 className="h1 text-[18px] font-bold mb-5">{t("content.almost_done_verify_phone")}</h1>
                            <div className="pb-4">{t("content.confirm_with_sms_code")}</div>
                            <div className={"my-5 text-center"}>
                                <Form
                                    form={form}
                                    layout="vertical"
                                    onFinish={onFinish}
                                    onFinishFailed={onFinishFailed}
                                    autoComplete="off"
                                >
                                    <Form.Item
                                        name="smsCode"
                                        label={t("labels.sms_code")}
                                        rules={[
                                            { required: true, message: t("messages.enter_verification_code_sms") },
                                            { min: 1, message: t("messages.enter_verification_code_sms") },
                                            { max: 50, message: t("messages.code_too_long") },
                                        ]}
                                    >
                                        <Input
                                            placeholder={t("labels.fill_in")}
                                            autoComplete="one-time-code"
                                            disabled={updateCascadeProcessMutation.isPending}
                                        />
                                    </Form.Item>
                                    <Form.Item>
                                        <div className="relative flex items-center justify-center">
                                            {!isSinglePostVerification && !cascadeItemIdFromUrl && (
                                                <button
                                                    type="button"
                                                    className="absolute left-0 cursor-pointer text-blue-400 hover:underline bg-transparent border-0 p-0"
                                                    disabled={updateCascadeProcessMutation.isPending}
                                                    onClick={() => {
                                                        form.resetFields();
                                                        setPostVerificationItem(null);
                                                        hasAutoSelected.current = true; // Prevent re-auto-selection
                                                    }}
                                                >
                                                    ← {t("labels.back")}
                                                </button>
                                            )}
                                            <Button
                                                type="primary"
                                                htmlType="submit"
                                                disabled={updateCascadeProcessMutation.isPending}
                                                loading={updateCascadeProcessMutation.isPending}
                                            >
                                                {t("labels.get_money")}
                                            </Button>
                                        </div>
                                    </Form.Item>
                                </Form>
                            </div>
                        </>
                    ) : ((cascadeItemIdFromUrl && !hasAutoSelected.current) || isSinglePostVerification) &&
                      !postVerificationItem ? (
                        <CascadeLoading />
                    ) : (
                        <>
                            {redirectItems.length > 0 ? (
                                <>
                                    <h1 className="h1 text-[18px] font-bold mb-5">
                                        🔍{" "}
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: t("content.found_best_offers_with_emoji"),
                                            }}
                                        />{" "}
                                        🔎
                                    </h1>
                                    <div className="pb-4">
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: t(
                                                    "content.complete_request_multiple_companies_with_formatting"
                                                ),
                                            }}
                                        />
                                    </div>
                                    <div className={"my-5"}>
                                        <Table
                                            className={"w-full"}
                                            bordered
                                            showHeader={false}
                                            pagination={false}
                                            columns={columns}
                                            dataSource={redirectItems}
                                        />
                                    </div>
                                </>
                            ) : (
                                <div className={"text-center"}>
                                    {" "}
                                    <CascadeFailed />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        );
    }
};
